import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';

const ContactBusinessInsurance = () => {
  const [searchParams] = useSearchParams();
  const preselectedSpecialization = searchParams.get('specialization') || '';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    specialization: preselectedSpecialization,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const specializations = [
    'Property & Casualty - Small/Mid-Market',
    'Construction Risk - Solutions',
    'Healthcare Risk - Solutions',
    'Transportation Risk - Solutions',
    'Manufacturing - & Industrial',
    'Professional - Services',
    'Retail - & Hospitality',
    'Non-Profit - Organizations',
    'Real Estate - Risk Solutions',
    'Employee - Benefits',
    'Financial Institutions - Risk Solutions',
    'Enterprise Insurance - for Small Business'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone || '',
        company: formData.company || '',
        specialization: formData.specialization || '',
        message: formData.message || ''
      });
      
      const response = await fetch(`${API_URL}/api/inquiries?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error('Failed to submit');
      
      toast.success('Thank you! We will contact you shortly.');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        specialization: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page Banner */}
      <div className="page-banner">
        <div className="container-wide">
          <h1>CONTACT US</h1>
          <div className="breadcrumb">
            <Link to="/">Home</Link> &gt; <Link to="/specializations">Areas of Specialization</Link> &gt; Contact
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-12" data-testid="contact-business-content">
        <div className="container-narrow">
          
          <div className="text-center mb-10">
            <h2 className="text-3xl font-serif text-[#1a365d] mb-4">Let Us Help You</h2>
            <p className="text-[var(--text-medium)] max-w-xl mx-auto">
              Our team of experienced insurance professionals is ready to help you find the right coverage 
              for your business. Fill out the form below and we'll get back to you within one business day.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="form-label" htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="form-input"
                  required
                  data-testid="input-first-name"
                />
              </div>
              <div>
                <label className="form-label" htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="form-input"
                  required
                  data-testid="input-last-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="form-label" htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  required
                  data-testid="input-email"
                />
              </div>
              <div>
                <label className="form-label" htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="input-phone"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="form-label" htmlFor="company">Company Name</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="form-input"
                data-testid="input-company"
              />
            </div>

            <div className="mb-6">
              <label className="form-label" htmlFor="specialization">Area of Interest *</label>
              <select
                id="specialization"
                name="specialization"
                value={formData.specialization}
                onChange={handleChange}
                className="form-input"
                required
                data-testid="select-specialization"
              >
                <option value="">Select an area...</option>
                {specializations.map((spec, index) => (
                  <option key={index} value={spec}>{spec}</option>
                ))}
              </select>
            </div>

            <div className="mb-8">
              <label className="form-label" htmlFor="message">How Can We Help You?</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="form-input"
                rows={5}
                placeholder="Tell us about your insurance needs..."
                data-testid="input-message"
              />
            </div>

            <div className="text-center">
              <button
                type="submit"
                className="btn-navy"
                disabled={isSubmitting}
                data-testid="submit-contact-form"
              >
                {isSubmitting ? 'Sending...' : 'Submit Request'}
              </button>
            </div>
          </form>

          {/* Contact Info */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="text-center">
              <h3 className="text-xl font-serif text-[#1a365d] mb-4">Prefer to Talk?</h3>
              <p className="text-[var(--text-medium)] mb-2">
                Email us directly at{' '}
                <a href="mailto:monte@archoninsurance.com" className="text-[var(--gold)] hover:underline">
                  monte@archoninsurance.com
                </a>
              </p>
              <p className="text-[var(--text-medium)]">
                Archon Insurance Agency | Massachusetts
              </p>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactBusinessInsurance;
